import { merge } from 'lodash';

import type { ProfilePageSettings } from 'src/components/Profile/CreateResume/reducer';
import type { Card, Site, WithTypeName } from 'src/types';

export const isEmpty = (obj?: Record<string, unknown> | null): boolean =>
  !(obj && Object.keys(obj).length > 0);

export const getQueryParams = (str = ''): Record<string, string> => {
  const params = str.replace('#', '').split('?')[1] || '';
  return params.split('&').reduce((acc, curr) => {
    const [key, value] = curr.split('=');
    const param = key ? { [key]: value } : {};
    return {
      ...acc,
      ...param,
    };
  }, {});
};

export const getProfileRoute = (
  path: string,
): { card: string; item: string } => {
  const exp = /\/profile\/me\/([a-z-]*)\/?([a-z0-9-]*).*/g;
  const match = exp.exec(path);
  return {
    card: match && match[1] ? match[1] : '',
    item: match && match[2] ? match[2] : '',
  };
};

export const parseUrl = ({
  pathname,
  search,
}: {
  pathname: string;
  search: string;
}): ProfilePageSettings => {
  const { card: cardPath, item: itemPath } = getProfileRoute(pathname);
  const queryParams = getQueryParams(decodeURIComponent(search));
  const { card, item }: { card?: Card; item?: string } = queryParams;
  return {
    ...queryParams,
    ...(cardPath ? { card: cardPath as Card } : { card }),
    ...(itemPath ? { item: itemPath } : { item }),
  };
};

export const wait = (fn: (...args: any[]) => void, time: number) =>
  setTimeout(fn, time);

export const removeDuplicateObjectsById = <T>(
  initialData: Array<T & { id: string }>,
  newData: Array<T & { id: string }>,
): T[] => {
  const ids = new Set(initialData.map((data: { id: string }) => data.id));
  return [
    ...initialData,
    ...newData.filter((data: { id: string }) => !ids.has(data.id)),
  ];
};

export const mergeItemIntoListById = <
  T extends { id: string } & Record<string, unknown>,
>({
  list,
  item,
  addToFrontOfList = false,
}: {
  list: T[];
  item: WithTypeName<T>;
  addToFrontOfList?: boolean;
}) => {
  const updatedList = [...list];
  const listIds = list.map((i) => i.id);
  if (listIds.indexOf(item.id) === -1) {
    if (addToFrontOfList) {
      updatedList.unshift(item);
    } else {
      updatedList.push(item);
    }
  }
  return updatedList.map((listItem) =>
    listItem.id === item.id ? merge(listItem, item) : listItem,
  );
};

export const getUnifiedSiteStatus = (site: Site) =>
  site.startsWith('candidate');
